import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import axios from 'axios';
import { Briefcase, User, Buildings, Phone, Envelope, ArrowRight, ArrowLeft, Eye, EyeSlash, Play, SignIn, Key, ShieldCheck, ArrowCounterClockwise } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ==================== DEMO FORM ====================
function DemoForm() {
  const [form, setForm] = useState({ name: '', company: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/demo-request`, form, { withCredentials: true });
      await checkAuth();
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setLoading(false); }
  };
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <>
      <p className="text-sm text-[#94A3B8] text-center mb-5">Fill in your details to explore the full platform</p>
      {error && <div className="mb-4 p-3 rounded-lg text-sm bg-red-500/10 border border-red-500/20 text-red-400" data-testid="demo-form-error">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="demo-request-form">
        <InputField icon={User} label="Full Name *" value={form.name} onChange={set('name')} placeholder="John Smith" testId="demo-name-input" />
        <InputField icon={Buildings} label="Company *" value={form.company} onChange={set('company')} placeholder="Acme Construction Ltd" testId="demo-company-input" />
        <InputField icon={Phone} label="Phone Number *" type="tel" value={form.phone} onChange={set('phone')} placeholder="+971 50 123 4567" testId="demo-phone-input" />
        <InputField icon={Envelope} label="Email Address *" type="email" value={form.email} onChange={set('email')} placeholder="john@company.com" testId="demo-email-input" />
        <SubmitBtn loading={loading} text="Access Live Demo" loadingText="Launching Demo..." testId="demo-submit-btn" />
      </form>
      <p className="text-center text-[10px] text-[#475569] mt-4">Your contact details will be shared with the ProjectBook Pro team.</p>
    </>
  );
}

// ==================== SIGN IN FORM ====================
function SignInForm({ onForgot }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally { setLoading(false); }
  };

  return (
    <>
      <p className="text-sm text-[#94A3B8] text-center mb-5">Sign in to your account</p>
      {error && <div className="mb-4 p-3 rounded-lg text-sm bg-red-500/10 border border-red-500/20 text-red-400" data-testid="login-error">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="signin-form">
        <InputField icon={Envelope} label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" testId="email-input" />
        <div>
          <label className="block text-xs font-medium text-[#94A3B8] mb-1.5">Password</label>
          <div className="relative">
            <input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Enter your password"
              className="w-full px-4 py-2.5 rounded-lg bg-[#1E293B] border border-[#334155] text-white text-sm placeholder:text-[#475569] focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent transition-all pr-10"
              data-testid="password-input" />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white transition-colors">
              {showPw ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <SubmitBtn loading={loading} text="Sign In" loadingText="Signing in..." testId="login-button" />
      </form>
      <button onClick={onForgot} className="w-full text-center text-xs text-[#64748B] hover:text-[#3B82F6] mt-4 transition-colors" data-testid="forgot-password-link">
        Forgot your password?
      </button>
    </>
  );
}

// ==================== FORGOT PASSWORD ====================
function ForgotPasswordForm({ onBack }) {
  const [step, setStep] = useState(1); // 1=email, 2=answer, 3=done
  const [email, setEmail] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);

  const handleGetQuestion = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/forgot-password/question`, { email });
      setQuestion(res.data.question);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to retrieve security question');
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password/reset`, { email, answer, new_password: newPassword });
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally { setLoading(false); }
  };

  if (step === 3) {
    return (
      <div className="text-center py-4">
        <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
          <ShieldCheck className="w-7 h-7 text-emerald-400" weight="fill" />
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">Password Reset Successful</h3>
        <p className="text-sm text-[#94A3B8] mb-5">You can now sign in with your new password.</p>
        <button onClick={onBack} className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-[#3B82F6] hover:bg-[#2563EB] transition-all" data-testid="back-to-signin-btn">
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onBack} className="text-[#64748B] hover:text-white transition-colors" data-testid="forgot-back-btn">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <p className="text-sm text-[#94A3B8]">{step === 1 ? 'Enter your email to recover your password' : 'Answer your security question'}</p>
      </div>
      {error && <div className="mb-4 p-3 rounded-lg text-sm bg-red-500/10 border border-red-500/20 text-red-400" data-testid="forgot-error">{error}</div>}

      {step === 1 && (
        <form onSubmit={handleGetQuestion} className="space-y-4" data-testid="forgot-step1">
          <InputField icon={Envelope} label="Email Address" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" testId="forgot-email-input" />
          <SubmitBtn loading={loading} text="Continue" loadingText="Looking up..." testId="forgot-continue-btn" />
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleResetPassword} className="space-y-4" data-testid="forgot-step2">
          <div className="p-3 rounded-lg bg-[#1E293B] border border-[#334155]">
            <p className="text-[10px] text-[#64748B] mb-1">Security Question</p>
            <p className="text-sm text-white font-medium">{question}</p>
          </div>
          <InputField icon={Key} label="Your Answer" value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Type your answer" testId="forgot-answer-input" />
          <div>
            <label className="block text-xs font-medium text-[#94A3B8] mb-1.5">New Password</label>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required placeholder="Minimum 6 characters" minLength={6}
                className="w-full px-4 py-2.5 rounded-lg bg-[#1E293B] border border-[#334155] text-white text-sm placeholder:text-[#475569] focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent transition-all pr-10"
                data-testid="forgot-new-password" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white">
                {showPw ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <InputField icon={Key} label="Confirm Password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" testId="forgot-confirm-password" />
          <SubmitBtn loading={loading} text="Reset Password" loadingText="Resetting..." testId="forgot-reset-btn" />
        </form>
      )}
    </>
  );
}

// ==================== SHARED UI ====================
function InputField({ icon: Icon, label, type = 'text', value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#94A3B8] mb-1.5">{label}</label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />}
        <input type={type} value={value} onChange={onChange} required placeholder={placeholder}
          className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-2.5 rounded-lg bg-[#1E293B] border border-[#334155] text-white text-sm placeholder:text-[#475569] focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent transition-all`}
          data-testid={testId} />
      </div>
    </div>
  );
}

function SubmitBtn({ loading, text, loadingText, testId }) {
  return (
    <button type="submit" disabled={loading}
      className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50"
      style={{ background: loading ? '#1E40AF' : 'linear-gradient(135deg, #3B82F6, #1D4ED8)' }}
      data-testid={testId}>
      {loading ? (
        <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> {loadingText}</>
      ) : (
        <>{text} <ArrowRight className="w-4 h-4" weight="bold" /></>
      )}
    </button>
  );
}

// ==================== MAIN PAGE ====================
export default function DemoRequestPage() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'signin' ? 'signin' : 'demo';
  const [tab, setTab] = useState(initialTab);
  const [showForgot, setShowForgot] = useState(false);
  const branding = useBranding();

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ backgroundColor: '#0B1120' }}>
      <div className="w-full max-w-md">
        <div className="rounded-2xl shadow-2xl border border-white/10 backdrop-blur-sm" style={{ backgroundColor: '#111827' }}>
          {/* Logo */}
          <div className="flex flex-col items-center pt-8 px-8">
            {branding.logo_url ? (
              <img src={`${API_URL}${branding.logo_url}`} alt="ProjectBook Pro" className="w-full max-w-[240px] rounded-xl object-contain mb-3 p-2" />
            ) : (
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-3 bg-gradient-to-br from-[#3B82F6] to-[#1D4ED8] shadow-lg shadow-blue-500/25">
                <Briefcase className="w-7 h-7 text-white" weight="bold" />
              </div>
            )}
          </div>

          {/* Tabs — hidden during forgot password */}
          {!showForgot && (
            <div className="flex mx-8 mt-4 mb-6 bg-[#0F172A] rounded-xl p-1 border border-white/5" data-testid="auth-tabs">
              <button onClick={() => setTab('demo')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${tab === 'demo' ? 'bg-[#3B82F6] text-white shadow-lg shadow-blue-500/20' : 'text-[#64748B] hover:text-[#94A3B8]'}`}
                data-testid="tab-demo">
                <Play className="w-4 h-4" weight="fill" /> Try Demo
              </button>
              <button onClick={() => setTab('signin')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${tab === 'signin' ? 'bg-[#3B82F6] text-white shadow-lg shadow-blue-500/20' : 'text-[#64748B] hover:text-[#94A3B8]'}`}
                data-testid="tab-signin">
                <SignIn className="w-4 h-4" /> Sign In
              </button>
            </div>
          )}

          {/* Forgot Password header */}
          {showForgot && (
            <div className="flex items-center justify-center gap-2 mx-8 mt-4 mb-6">
              <ArrowCounterClockwise className="w-5 h-5 text-[#F59E0B]" />
              <h2 className="text-base font-semibold text-white">Reset Password</h2>
            </div>
          )}

          {/* Tab Content */}
          <div className="px-8 pb-8">
            {showForgot ? (
              <ForgotPasswordForm onBack={() => setShowForgot(false)} />
            ) : tab === 'demo' ? (
              <DemoForm />
            ) : (
              <SignInForm onForgot={() => setShowForgot(true)} />
            )}
          </div>
        </div>

        {/* Back to website */}
        <div className="mt-5 text-center">
          <Link to="/website" className="inline-flex items-center gap-1.5 text-sm text-[#64748B] hover:text-white transition-colors" data-testid="back-to-website-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Website
          </Link>
        </div>
      </div>
    </div>
  );
}
